import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useDispatch, useSelector } from "react-redux";

import MoreVertIcon from "@material-ui/icons/MoreVert";

import {
  Layout,
  MenuItemLink,
  Notification,
  UserMenu,
  useNotify,
} from "react-admin";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import { makeStyles, useMediaQuery } from "@material-ui/core";

import DefaultError from "../../components/DefaultError";
import {
  AppInfoIcon,
  BugIcon,
  ContrastIcon,
  DesktopIcon,
  LogoutIcon,
  MobileIcon,
  ProfileIcon,
  RoadmapIcon,
  ThemeToggleIcon,
} from "../../components/common/iconography";
import { AppInfoDialog } from "../../components/common/AppInfoDialog";
import { MenuItemClickable } from "../../components/MenuItemClickable";

import { PortalType } from "../../utils/constants";
import {
  getActivePortalType,
  isSubscriptionDisabled,
  logError,
} from "../../utils/appUtils";
import { buildRoadmapUrl, ensureProduktlyToken } from "../../utils/produktlyUtils";

import { CrewMenu } from "../../crewApp/layout/CrewMenu";
import { useThemeState } from "../../ThemeStateProvider";
import { ManagerAppBar } from "./ManagerAppBar";
import { ManagerMenu } from "./ManagerMenu";
import { KB_CHAT_PUSH_PX } from "../../utils/constants";

const useStyles = makeStyles(() => ({
  kbChatOpen: {
    // Add a little extra breathing room beyond the drawer width so content never feels
    // tight against the drawer edge (roughly the width of the collapsed menu).
    // (Fixed at 25px for consistent feel across themes.)
    // React-admin v3 uses generated JSS classnames (e.g., RaLayout-content-25) that
    // change on refresh. Target stable hooks instead.
    "& #main-content": {
      paddingRight: `${KB_CHAT_PUSH_PX}px`,
      boxSizing: "border-box",
      transition: "padding-right 300ms ease",
    },
    "& [class^='RaAppBar-root']": {
      right: `${KB_CHAT_PUSH_PX}px`,
      width: `calc(100% - ${KB_CHAT_PUSH_PX}px)`,
      transition: "width 300ms ease, right 300ms ease",
    },
  },
}));

const ManagerUserMenu = ({
  portalType,
  setPortalType,
  toggleAppInfoOpen,
  ...props
}) => {
  const [selectedApp, setSelectedApp] = useState(portalType);
  const [roadmapDialogOpen, setRoadmapDialogOpen] = useState(false);
  const profilePath = `/users/${window.appUser?.id}`;
  const auth = useAuth();
  const history = useHistory();
  const notify = useNotify();
  const { toggleContrast, togglePalette } = useThemeState();
  const handleAppChange = () => {
    const value =
      selectedApp === PortalType.MANAGER ? PortalType.CREW : PortalType.MANAGER;
    try {
      localStorage.setItem("portalType", value);
      window.appUser.portalType = value;
      setSelectedApp(value);
      setPortalType(value);
      history.push("/");
    } catch (err) {
      logError("ManagerUserMenu:handleAppChange", err, false);
    }
  };

  const handleBugReport = () => {
    window.open("https://transloads.co/submit-a-ticket/", "_blank");
  };

  const handleRoadmap = async () => {
    try {
      setRoadmapDialogOpen(true);
      const produktlyToken = await ensureProduktlyToken(auth.user?.access_token);
      window.open(buildRoadmapUrl(produktlyToken), "_blank");
    } catch (error) {
      console.error("Roadmap Sign-in Error:", error);
      notify("Failed to open Roadmap. Please try again.", "error");
    } finally {
      setRoadmapDialogOpen(false);
    }
  };

  return (
    <>
      <UserMenu label="USER" icon={<MoreVertIcon />}>
        {selectedApp === PortalType.CREW ? (
          <MenuItemClickable
            primaryText="Manager App"
            leftIcon={<DesktopIcon />}
            clickHandler={handleAppChange}
          />
        ) : (
          <MenuItemClickable
            primaryText="Operator App"
            leftIcon={<MobileIcon />}
            clickHandler={handleAppChange}
          />
        )}
        <MenuItemLink
          to={profilePath}
          primaryText="Profile"
          leftIcon={<ProfileIcon />}
        />
        <MenuItemClickable
          primaryText="Theme"
          leftIcon={<ThemeToggleIcon />}
          clickHandler={togglePalette}
        />
        <MenuItemClickable
          primaryText="Contrast"
          leftIcon={<ContrastIcon />}
          clickHandler={toggleContrast}
        />
        <MenuItemClickable
          primaryText="App Info"
          leftIcon={<AppInfoIcon />}
          clickHandler={toggleAppInfoOpen}
        />
        <MenuItemClickable
          primaryText="Roadmap"
          leftIcon={<RoadmapIcon />}
          clickHandler={handleRoadmap}
        />
        <MenuItemClickable
          primaryText="Log a Ticket"
          leftIcon={<BugIcon />}
          clickHandler={handleBugReport}
        />
        <MenuItemLink
          to="/logout"
          primaryText="Logout"
          leftIcon={<LogoutIcon />}
        />
      </UserMenu>
      <Dialog
        open={roadmapDialogOpen}
        onClose={() => {}}
        aria-labelledby="roadmap-loading-title"
      >
        <DialogTitle id="roadmap-loading-title">
          Preparing to Open Roadmap
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minWidth={260}
            minHeight={120}
          >
            <CircularProgress size={32} />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Shared with the pages rendered under this layout — the Gate Queue's Open
// button leads somewhere different in each portal, so it has to resolve the
// portal exactly as the menu beside it does.
const getDefaultPortal = getActivePortalType;

export const ManagerLayout = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const sidebarOpen = useSelector((state) => state?.admin?.ui?.sidebarOpen);
  const didCollapseSidebarForChatRef = useRef(false);

  const [portalType, setPortalType] = useState(getDefaultPortal());
  const [appInfoOpen, setAppInfoOpen] = useState(false);
  const [isKbChatOpen, setIsKbChatOpen] = useState(false);
  const toggleAppInfoOpen = (_, reason) => {
    if (reason !== "backdropClick") setAppInfoOpen(!appInfoOpen);
  };

  useEffect(() => {
    if (isMobile) return;
    if (isKbChatOpen) {
      if (!sidebarOpen) return;
      didCollapseSidebarForChatRef.current = true;
      dispatch({ type: "RA/SET_SIDEBAR_VISIBILITY", payload: false });
      return;
    }

    if (!didCollapseSidebarForChatRef.current) return;
    didCollapseSidebarForChatRef.current = false;
    dispatch({ type: "RA/SET_SIDEBAR_VISIBILITY", payload: true });
  }, [dispatch, isKbChatOpen, isMobile, sidebarOpen]);

  const userMenu = (
    <ManagerUserMenu
      portalType={portalType}
      setPortalType={setPortalType}
      toggleAppInfoOpen={toggleAppInfoOpen}
    />
  );

  const kbChat = useMemo(
    () => ({
      onOpenChange: (isOpen) => setIsKbChatOpen(Boolean(isOpen)),
    }),
    [],
  );
  const notify = useNotify();
  if (isSubscriptionDisabled()) {
    notify(
      "Subscription inactive. For help contact support@transloads.co",
      "error",
    );
  }

  const wrapperClassName =
    isKbChatOpen && !isMobile ? classes.kbChatOpen : undefined;

  return (
    <div className={wrapperClassName}>
      <Layout
        {...props}
        appBar={(props) => ManagerAppBar(props, userMenu, kbChat)}
        menu={portalType === PortalType.CREW ? CrewMenu : ManagerMenu}
        notification={Notification}
        error={DefaultError}
      />
      <AppInfoDialog isOpen={appInfoOpen} onClose={toggleAppInfoOpen} />
    </div>
  );
};
